import React, { useEffect, useMemo, useState } from 'react';
import { Avatar, Button, Card, Col, DatePicker, Divider, List, PageHeader, Row, Space, Statistic, Table, Typography } from 'antd';
import { Provider } from 'jotai';
import { useMediaQuery } from 'react-responsive';
import { useRendimientoPage } from './hooks';
import { CalendarOutlined, ClearOutlined, LineChartOutlined } from "@ant-design/icons";
import moment from 'moment';

const { RangePicker } = DatePicker;
const COLORES = ['#0077b6', '#3f8600', '#cf1322', '#722ed1', '#fa8c16', '#08979c'];
const FORMATO_FECHA = 'DD/MM/YYYY';

const RendimientoPage = () => {
    const esDesktop = useMediaQuery({ query: "(min-width: 860px)" });
    const esMovil = useMediaQuery({ query: "(max-width: 860px)" });

    return (
        <Provider>
            {esDesktop && <RendimientoWeb />}
            {esMovil && <RendimientoMovil />}
        </Provider>
    );
};

function calcularTotalesPorLabor(datos) {
    const map = {};
    for (const item of datos) {
        if (!map[item.labor]) {
            map[item.labor] = { labor: item.labor, medicion: item.medicion, total: 0, registros: 0 };
        }
        map[item.labor].total += item.rendimiento ?? 0;
        map[item.labor].registros += 1;
    }
    return Object.values(map);
}

function aplicarFiltroFecha(datos, rango) {
    if (!rango || !rango[0] || !rango[1]) return datos;
    const desde = rango[0].startOf('day');
    const hasta = rango[1].endOf('day');
    return datos.filter(item => {
        const fecha = moment(item.fecha);
        return fecha.isSameOrAfter(desde) && fecha.isSameOrBefore(hasta);
    });
}

const TotalesPorLabor = ({ datos }) => {
    const totales = useMemo(() => calcularTotalesPorLabor(datos), [datos]);
    if (totales.length === 0) return null;

    return (
        <Row gutter={[16, 16]} style={{ marginBottom: 8 }}>
            {totales.map((item, idx) => (
                <Col xs={24} sm={12} md={8} lg={6} key={item.labor}>
                    <Card bordered={false} style={{ borderLeft: `4px solid ${COLORES[idx % COLORES.length]}` }}>
                        <Statistic
                            title={item.labor}
                            value={item.total}
                            suffix={item.medicion}
                            valueStyle={{ color: COLORES[idx % COLORES.length], fontSize: 22 }}
                            prefix={<LineChartOutlined />}
                        />
                        <div style={{ marginTop: 4, fontSize: 12, color: '#8c8c8c' }}>
                            {item.registros} {item.registros === 1 ? 'registro' : 'registros'}
                        </div>
                    </Card>
                </Col>
            ))}
        </Row>
    );
};

const FiltroPorFecha = ({ rango, onChange, onLimpiar, totalFiltrado, totalGeneral }) => {
    const filtrado = totalFiltrado < totalGeneral;
    return (
        <Card bordered={false} style={{ marginBottom: 12, padding: 0 }}>
            <Row align="middle" gutter={[16, 8]}>
                <Col>
                    <Space>
                        <CalendarOutlined style={{ color: '#595959' }} />
                        <Typography.Text strong>Filtrar por fecha:</Typography.Text>
                    </Space>
                </Col>
                <Col>
                    <RangePicker
                        value={rango}
                        onChange={onChange}
                        format={FORMATO_FECHA}
                        placeholder={['Desde', 'Hasta']}
                        allowClear={false}
                    />
                </Col>
                <Col>
                    <Button
                        icon={<ClearOutlined />}
                        onClick={onLimpiar}
                        disabled={!rango}
                    >
                        Limpiar
                    </Button>
                </Col>
                {filtrado && (
                    <Col>
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                            Mostrando {totalFiltrado} de {totalGeneral} registros
                        </Typography.Text>
                    </Col>
                )}
            </Row>
        </Card>
    );
};

const RendimientoWeb = () => {

    const { rendimiento, fetchRendimiento } = useRendimientoPage();
    const [rango, setRango] = useState(null);

    useEffect(() => {
        fetchRendimiento();
    }, []);

    const datosFiltrados = useMemo(() => aplicarFiltroFecha(rendimiento, rango), [rendimiento, rango]);

    const columnas = [
        { title: "Labor", dataIndex: "labor" },
        { title: "Año", dataIndex: "anio", width: '8%' },
        { title: "Fecha", dataIndex: "fecha", width: '12%', render: (fecha) => fecha ? moment(fecha).format('DD/MM/YYYY') : '' },
        { title: "Medición", dataIndex: "medicion", width: '12%' },
        { title: "Rendimiento", dataIndex: "rendimiento", width: '12%' },
    ];

    return (
        <>
            <PageHeader title="Mis Rendimientos" style={{ paddingLeft: "0px" }} />

            <FiltroPorFecha
                rango={rango}
                onChange={(val) => setRango(val)}
                onLimpiar={() => setRango(null)}
                totalFiltrado={datosFiltrados.length}
                totalGeneral={rendimiento.length}
            />

            <TotalesPorLabor datos={datosFiltrados} />

            <Card bordered={false}>
                <Divider orientation="left" style={{ marginTop: 0 }}>Detalle</Divider>
                <Table
                    rowKey={(item) => item.id}
                    dataSource={datosFiltrados}
                    columns={columnas}
                    size='small'
                    pagination={false}
                />
            </Card>
        </>
    );
};

const RendimientoMovil = () => {

    const { rendimiento, fetchRendimiento } = useRendimientoPage();
    const [rango, setRango] = useState(null);

    useEffect(() => {
        fetchRendimiento();
    }, []);

    const datosFiltrados = useMemo(() => aplicarFiltroFecha(rendimiento, rango), [rendimiento, rango]);
    const totales = useMemo(() => calcularTotalesPorLabor(datosFiltrados), [datosFiltrados]);

    return (
        <>
            <div className="container-movil-pdf-header" style={{ display: "flex", justifyContent: "space-between" }}>
                <div>Mis Rendimientos</div>
            </div>

            <div style={{ padding: "10px 15px 0" }}>
                <RangePicker
                    value={rango}
                    onChange={(val) => setRango(val)}
                    format={FORMATO_FECHA}
                    placeholder={['Desde', 'Hasta']}
                    allowClear={false}
                    style={{ width: '100%' }}
                />
                {rango && (
                    <Row justify="space-between" align="middle" style={{ marginTop: 6 }}>
                        <Col>
                            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                {datosFiltrados.length} de {rendimiento.length} registros
                            </Typography.Text>
                        </Col>
                        <Col>
                            <Button size="small" icon={<ClearOutlined />} onClick={() => setRango(null)}>
                                Limpiar
                            </Button>
                        </Col>
                    </Row>
                )}
            </div>

            {totales.length > 0 && (
                <div style={{ padding: "10px 15px 0" }}>
                    <Row gutter={[12, 12]}>
                        {totales.map((item, idx) => (
                            <Col span={12} key={item.labor}>
                                <Card
                                    bordered={false}
                                    size="small"
                                    style={{ borderLeft: `3px solid ${COLORES[idx % COLORES.length]}`, textAlign: 'center' }}
                                >
                                    <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 2 }}>{item.labor}</div>
                                    <div style={{ fontSize: 20, fontWeight: 700, color: COLORES[idx % COLORES.length] }}>
                                        {item.total}
                                    </div>
                                    <div style={{ fontSize: 11, color: '#595959' }}>{item.medicion}</div>
                                </Card>
                            </Col>
                        ))}
                    </Row>
                    <Divider style={{ margin: '12px 0 0' }} />
                </div>
            )}

            <div style={{ padding: "0 15px 15px" }}>
                <List
                    itemLayout="horizontal"
                    dataSource={datosFiltrados}
                    locale={{ emptyText: 'Sin registros para el período seleccionado' }}
                    renderItem={(item, idx) => (
                        <List.Item style={{ padding: "10px 0" }}>
                            <List.Item.Meta
                                avatar={
                                    <Avatar
                                        style={{ backgroundColor: COLORES[idx % COLORES.length] }}
                                        icon={<LineChartOutlined />}
                                    />
                                }
                                title={
                                    <div>
                                        <Typography.Text strong>{item.labor}</Typography.Text>
                                        <div style={{ fontSize: 12, color: '#1677ff' }}>{item.fecha ? moment(item.fecha).format('DD/MM/YYYY') : ''}</div>
                                    </div>
                                }
                                description={
                                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                        {item.medicion}
                                    </Typography.Text>
                                }
                            />
                            <div style={{ fontSize: 18, fontWeight: 600, padding: "0 10px" }}>
                                {item.rendimiento}
                            </div>
                        </List.Item>
                    )}
                />
            </div>
        </>
    );
};

export default RendimientoPage;
